import { useState, type ReactNode } from "react";
import type { Product } from "@outpost/shared";

export const GOOGLE_CALLBACK_HINT =
  "https://outpost16.com/v1/integrations/google/oauth/callback";

function cleanSecret(raw: string): string {
  return raw.trim().replace(/^["']+|["']+$/g, "");
}

function parseClientJson(
  raw: string,
): { ok: true; clientId: string; clientSecret: string } | { ok: false; message: string } {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const nested =
      (parsed.web as Record<string, unknown> | undefined) ??
      (parsed.installed as Record<string, unknown> | undefined) ??
      parsed;
    const clientId = cleanSecret(
      String(nested.client_id ?? nested.clientId ?? ""),
    );
    const clientSecret = cleanSecret(
      String(nested.client_secret ?? nested.clientSecret ?? ""),
    );
    if (!clientId || !clientSecret) {
      return {
        ok: false,
        message: "JSON needs client_id and client_secret.",
      };
    }
    return { ok: true, clientId, clientSecret };
  } catch {
    return { ok: false, message: "That isn’t valid JSON." };
  }
}

export type PropertyRow = {
  propertyId: string;
  productId: string;
};

export type SiteRow = {
  siteUrl: string;
  productId: string;
};

export type GoogleWizardSurface = "ga" | "gsc";

type Props = {
  /** Which connection card this wizard is under — mappings are scoped; OAuth is shared. */
  surface: GoogleWizardSurface;
  products: Product[];
  googleClientId: string;
  googleClientSecret: string;
  /** Secrets already vaulted from a prior authorize — skip JSON step. */
  credentialsStored?: boolean;
  busy: boolean;
  actionError: string;
  initialProperties?: PropertyRow[];
  initialSites?: SiteRow[];
  onGoogleClientId: (v: string) => void;
  onGoogleClientSecret: (v: string) => void;
  onAuthorize: (payload: {
    properties: PropertyRow[];
    sites: SiteRow[];
  }) => void;
  /** Re-map properties/sites without a new Google consent when secrets exist. */
  onSaveMappings?: (
    payload: {
      properties: PropertyRow[];
      sites: SiteRow[];
    },
    surface: GoogleWizardSurface,
  ) => void;
};

function Field(props: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="op-conn-field">
      <span className="op-conn-field-label">{props.label}</span>
      {props.children}
      {props.hint ? (
        <small className="op-conn-field-hint">{props.hint}</small>
      ) : null}
    </label>
  );
}

export function GoogleConnectWizard(props: Props) {
  const isGa = props.surface === "ga";
  const hasPaste =
    Boolean(props.googleClientId.trim()) &&
    Boolean(props.googleClientSecret.trim());
  const hasCreds = hasPaste || Boolean(props.credentialsStored);
  const [step, setStep] = useState<1 | 2 | 3>(() =>
    props.credentialsStored ? 3 : hasPaste ? 2 : 1,
  );
  const [jsonDraft, setJsonDraft] = useState("");
  const [jsonNotice, setJsonNotice] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [checklistOk, setChecklistOk] = useState(
    Boolean(props.credentialsStored),
  );
  const [properties, setProperties] = useState<PropertyRow[]>(
    props.initialProperties?.length
      ? props.initialProperties
      : [{ propertyId: "", productId: props.products[0]?.id ?? "" }],
  );
  const [sites, setSites] = useState<SiteRow[]>(
    props.initialSites?.length
      ? props.initialSites
      : [{ siteUrl: "", productId: props.products[0]?.id ?? "" }],
  );

  function applyJson(raw: string): boolean {
    const result = parseClientJson(raw);
    if (!result.ok) {
      setJsonError(result.message);
      setJsonNotice("");
      return false;
    }
    props.onGoogleClientId(result.clientId);
    props.onGoogleClientSecret(result.clientSecret);
    setJsonError("");
    setJsonNotice("Client ID and secret loaded.");
    setJsonDraft("");
    setStep(2);
    return true;
  }

  const validProperties = properties.filter(
    (p) => p.propertyId.trim() && p.productId,
  );
  const validSites = sites.filter((s) => s.siteUrl.trim() && s.productId);

  /** Keep the other surface’s existing mappings when saving from one card. */
  const preservedProperties =
    props.initialProperties?.filter((p) => p.propertyId.trim() && p.productId) ??
    [];
  const preservedSites =
    props.initialSites?.filter((s) => s.siteUrl.trim() && s.productId) ?? [];

  const payloadProperties = isGa ? validProperties : preservedProperties;
  const payloadSites = isGa ? preservedSites : validSites;

  const mappingsReady = isGa
    ? validProperties.length > 0
    : validSites.length > 0;
  const canAuthorize = hasCreds && checklistOk && mappingsReady && !props.busy;
  const canSaveMappings =
    Boolean(props.credentialsStored) &&
    Boolean(props.onSaveMappings) &&
    mappingsReady &&
    !props.busy;

  function emitPayload() {
    return {
      properties: payloadProperties.map((p) => ({
        propertyId: cleanSecret(p.propertyId),
        productId: p.productId,
      })),
      sites: payloadSites.map((s) => ({
        siteUrl: s.siteUrl.trim(),
        productId: s.productId,
      })),
    };
  }

  return (
    <div className="op-google-wizard">
      {!props.credentialsStored ? (
        <ol className="op-google-steps" aria-label="Google connect steps">
          <li className={step === 1 ? "is-current" : step > 1 ? "is-done" : ""}>
            1. Credentials
          </li>
          <li className={step === 2 ? "is-current" : step > 2 ? "is-done" : ""}>
            2. Cloud Console
          </li>
          <li className={step === 3 ? "is-current" : ""}>
            3. {isGa ? "Properties" : "Sites"}
          </li>
        </ol>
      ) : null}

      {props.credentialsStored ? (
        <p className="op-conn-hint op-conn-hint-ok">
          Shared Google OAuth is vaulted — used by both Analytics and Search
          Console. {isGa ? "Edit GA4 property mappings below." : "Edit site mappings below."}
        </p>
      ) : (
        <p className="op-conn-hint">
          One OAuth client lights both Google Analytics and Search Console.
          Configure {isGa ? "GA4 properties" : "Search Console sites"} here;
          map the other from its own connection card.
        </p>
      )}

      {step === 1 && !props.credentialsStored ? (
        <div className="op-google-step-body">
          <p className="op-conn-hint">
            Upload the OAuth client JSON from Google Cloud (Web application).
            Only the client ID and secret are read — nothing else is shown until
            this step succeeds.
          </p>
          <div className="op-conn-json">
            <div className="op-conn-json-head">
              <span className="op-conn-field-label">Credentials JSON</span>
              <div className="op-conn-json-actions">
                <label className="op-btn ghost">
                  Upload .json
                  <input
                    type="file"
                    accept="application/json,.json,text/plain"
                    className="op-conn-file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        const text =
                          typeof reader.result === "string" ? reader.result : "";
                        setJsonDraft(text);
                        applyJson(text);
                      };
                      reader.readAsText(file);
                    }}
                  />
                </label>
                <button
                  type="button"
                  className="op-btn ghost"
                  disabled={!jsonDraft.trim()}
                  onClick={() => applyJson(jsonDraft)}
                >
                  Apply paste
                </button>
              </div>
            </div>
            <textarea
              className="op-field op-conn-json-area"
              value={jsonDraft}
              onChange={(e) => {
                setJsonDraft(e.target.value);
                setJsonError("");
                setJsonNotice("");
              }}
              placeholder='Paste {"web":{"client_id":"…","client_secret":"…"}}'
              rows={5}
              spellCheck={false}
              autoComplete="off"
            />
            {jsonNotice ? (
              <p className="op-conn-hint op-conn-hint-ok">{jsonNotice}</p>
            ) : null}
            {jsonError ? (
              <p className="op-notice err">{jsonError}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === 2 && !props.credentialsStored ? (
        <div className="op-google-step-body">
          <p className="op-conn-hint">
            Do this once in Google Cloud Console before authorizing:
          </p>
          <ol className="op-google-checklist">
            <li>
              OAuth client type must be <strong>Web application</strong>.
            </li>
            <li>
              Add this exact Authorized redirect URI (no trailing slash):
              <div className="op-conn-redirect-row" style={{ marginTop: 8 }}>
                <code className="op-conn-redirect-uri">
                  {GOOGLE_CALLBACK_HINT}
                </code>
                <button
                  type="button"
                  className="op-btn ghost"
                  onClick={() => {
                    void navigator.clipboard?.writeText(GOOGLE_CALLBACK_HINT);
                  }}
                >
                  Copy
                </button>
              </div>
            </li>
            <li>
              OAuth consent screen → Publishing <strong>Testing</strong> → add
              your Google account under <strong>Test users</strong>.
            </li>
            <li>
              Enable <strong>Google Analytics Data API</strong> and{" "}
              <strong>Search Console API</strong> for the project.
            </li>
          </ol>
          <label className="op-google-check">
            <input
              type="checkbox"
              checked={checklistOk}
              onChange={(e) => setChecklistOk(e.target.checked)}
            />
            I’ve completed these Cloud Console steps
          </label>
          <div className="op-item-actions">
            <button
              type="button"
              className="op-btn ghost"
              onClick={() => setStep(1)}
            >
              Back
            </button>
            <button
              type="button"
              className="op-primary"
              disabled={!checklistOk}
              onClick={() => setStep(3)}
            >
              Continue to {isGa ? "properties" : "sites"}
            </button>
          </div>
        </div>
      ) : null}

      {step === 3 || props.credentialsStored ? (
        <div className="op-google-step-body">
          {isGa ? (
            <div className="op-google-map-block">
              <div className="op-google-map-head">
                <strong>GA4 properties</strong>
              </div>
              {properties.map((row, idx) => (
                <div key={`prop-${idx}`} className="op-google-map-row">
                  <Field label="Property ID">
                    <input
                      className="op-field"
                      value={row.propertyId}
                      onChange={(e) => {
                        const v = cleanSecret(e.target.value);
                        setProperties((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, propertyId: v } : r,
                          ),
                        );
                      }}
                      placeholder="123456789"
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Product">
                    <select
                      className="op-field"
                      value={row.productId}
                      onChange={(e) => {
                        const v = e.target.value;
                        setProperties((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, productId: v } : r,
                          ),
                        );
                      }}
                    >
                      {props.products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {properties.length > 1 ? (
                    <button
                      type="button"
                      className="op-btn ghost"
                      onClick={() =>
                        setProperties((rows) =>
                          rows.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="op-btn ghost op-google-map-add"
                onClick={() =>
                  setProperties((rows) => [
                    ...rows,
                    {
                      propertyId: "",
                      productId: props.products[0]?.id ?? "",
                    },
                  ])
                }
              >
                Add property
              </button>
            </div>
          ) : (
            <div className="op-google-map-block">
              <div className="op-google-map-head">
                <strong>Search Console sites</strong>
              </div>
              {sites.map((row, idx) => (
                <div key={`site-${idx}`} className="op-google-map-row">
                  <Field
                    label="Site URL"
                    hint="Must match Search Console exactly — often https://example.com/ (trailing slash) or sc-domain:example.com"
                  >
                    <input
                      className="op-field"
                      value={row.siteUrl}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSites((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, siteUrl: v } : r,
                          ),
                        );
                      }}
                      placeholder="https://example.com/ or sc-domain:example.com"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Product">
                    <select
                      className="op-field"
                      value={row.productId}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSites((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, productId: v } : r,
                          ),
                        );
                      }}
                    >
                      {props.products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {sites.length > 1 ? (
                    <button
                      type="button"
                      className="op-btn ghost"
                      onClick={() =>
                        setSites((rows) => rows.filter((_, i) => i !== idx))
                      }
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="op-btn ghost op-google-map-add"
                onClick={() =>
                  setSites((rows) => [
                    ...rows,
                    { siteUrl: "", productId: props.products[0]?.id ?? "" },
                  ])
                }
              >
                Add site
              </button>
            </div>
          )}

          {props.actionError ? (
            <p className="op-notice err" role="alert">
              {props.actionError}
            </p>
          ) : null}

          <div className="op-item-actions">
            {!props.credentialsStored ? (
              <button
                type="button"
                className="op-btn ghost"
                onClick={() => setStep(2)}
              >
                Back
              </button>
            ) : null}
            {canSaveMappings ? (
              <button
                type="button"
                className="op-btn ghost"
                disabled={!canSaveMappings}
                onClick={() => props.onSaveMappings?.(emitPayload(), props.surface)}
              >
                {props.busy ? "Saving…" : "Save mappings"}
              </button>
            ) : null}
            <button
              type="button"
              className="op-primary"
              disabled={!canAuthorize}
              onClick={() => props.onAuthorize(emitPayload())}
            >
              {props.busy ? "Opening Google…" : "Authorize with Google"}
            </button>
          </div>
          {!hasCreds ? (
            <p className="op-notice err" role="status">
              Upload credentials JSON in step 1 before authorizing.
            </p>
          ) : null}
          {hasCreds && !checklistOk ? (
            <p className="op-notice err" role="status">
              Confirm the Cloud Console checklist in step 2 first.
            </p>
          ) : null}
          {hasCreds && checklistOk && !mappingsReady ? (
            <p className="op-notice err" role="status">
              {isGa
                ? "Add at least one GA4 property."
                : "Add at least one Search Console site."}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
